import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { FC, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrderContext, useCommonContext } from '../hooks/useContexts';
import { instoreOrderNumberFormat } from '../components/shared/order-number-formatter/OrderNumberFormatter';
import { GetOrderDetailsInfo } from '../hooks/order-details-hooks';
import useFetchState from '../hooks/useFetchState';
import { IAlert } from '../models/common';
import {
  IOrderDetailsRequest,
  IOrderDetailsRequestParams,
} from '../models/order-details';
import { ISORISOrderNumberBarcodeResponse } from '../models/soris';
import { getOrderType } from '../utils/order-details/OrderDetailsUtils';
import { OrderDetailsContext } from './OrderDetailsContext';

export type { IOrderDetailsContext } from './OrderDetailsContext';

interface IOrderDetailsProvider {
  children: ReactNode;
  customerType: 'b2b' | 'b2c';
  customerRole: string;
}

export const OrderDetailsProvider: FC<IOrderDetailsProvider> = ({
  children,
  customerType,
  customerRole,
}) => {
  // Context's
  const { svocID } = useThdCustomer();
  // State
  const [alertState, setAlertState] = useState<IAlert | null>(null);
  const [exportAlertState, setExportAlertState] = useState<IAlert | null>(null);
  const { setCancellationReasonCodes, setReasonCodesApiError } =
    useOrderContext();

  // Misc
  const location = useLocation();
  const { timezone } = useCommonContext();
  const urlParams = new URLSearchParams(location.search);

  let orderNumber = String(urlParams.get('orderNumber'));
  const orderIndex = Number(urlParams.get('index'));
  const salesDate = String(urlParams.get('salesDate'));
  const storeNumber = Number(urlParams.get('storeNumber'));
  const transactionId = Number(urlParams.get('transactionId'));
  const registerNumber = Number(urlParams.get('registerNumber'));
  const transactionType = String(urlParams.get('transactionType'));
  const nickName = String(urlParams.get('nickName'));
  const localDelivery = Boolean(urlParams.get('localDelivery'));

  orderNumber =
    orderNumber === 'null'
      ? instoreOrderNumberFormat(registerNumber, transactionId, customerType)
      : orderNumber;

  // Generate the request based on the customer type
  const generateOrderDetailsRequest = (): IOrderDetailsRequest => {
    const orderType = getOrderType(orderNumber);

    if (customerType === 'b2b' && orderType === 'ONLINE') {
      if (transactionType === 'R') {
        return {
          orderDetailsRequest: {
            searchType: 'online',
            registerNumber: registerNumber ?? 0,
            salesDate: salesDate ?? '',
            storeNumber: storeNumber ?? 0,
            transactionId: transactionId ?? 0,
            transactionType: transactionType ?? '',
            orderNumber: orderNumber ?? '',
            customerAccountId: svocID,
            groupAndSortBy: {
              groupBy: 'fulfillmentType',
              filterResponse: true,
            },
            requestOriginatedFrom: 'orderDetails',
            timezone,
          },
        };
      }
      return {
        orderDetailsRequest: {
          searchType: 'online',
          orderNumber: orderNumber ?? '',
          customerAccountId: svocID,
          groupAndSortBy: {
            groupBy: 'fulfillmentType',
            filterResponse: true,
          },
          requestOriginatedFrom: 'orderDetails',
          timezone,
        },
      };
    }
    if (customerType === 'b2b' && orderType === 'INSTORE') {
      return {
        orderDetailsRequest: {
          searchType: 'instore',
          registerNumber: registerNumber ?? 0,
          salesDate: salesDate ?? '',
          storeNumber: storeNumber ?? 0,
          transactionId: transactionId ?? 0,
          orderNumber: orderNumber ?? '',
          customerAccountId: svocID,
          groupAndSortBy: {
            groupBy: 'fulfillmentType',
            filterResponse: true,
          },
          requestOriginatedFrom: 'orderDetails',
          timezone,
        },
      };
    }

    return {
      orderDetailsRequest: {
        searchType: orderType?.toLowerCase(),
        orderNumber: orderNumber ?? '',
        registerNumber,
        storeNumber,
        transactionId,
        salesDate,
        transactionType,
        requestOriginatedFrom: 'orderDetails',
        timezone,
      },
    };
  };

  // Initializing the order barcode API state hook
  const orderBarcodeApiState =
    useFetchState<ISORISOrderNumberBarcodeResponse>();

  const {
    orderDetailsData,
    orderDetailsError,
    orderDetailsLoaded,
    deliveryTrackerLoaded,
  } = GetOrderDetailsInfo(
    customerType,
    customerRole,
    generateOrderDetailsRequest(),
    setCancellationReasonCodes,
    setReasonCodesApiError,
    localDelivery,
  );

  // Used to pass the request params to the children
  const orderDetailsRequestParams: IOrderDetailsRequestParams = {
    orderDetailsRoute: location.pathname,
    orderNumber:
      orderDetailsData?.orderNumber ??
      orderDetailsData?.orderNumbers?.[0] ??
      orderNumber,
    salesDate,
    storeNumber,
    transactionId,
    registerNumber,
    orderIndex,
    customerAccountId: svocID,
    timezone,
  };

  return (
    <OrderDetailsContext.Provider
      value={{
        orderDetailsData,
        orderDetailsError,
        orderDetailsLoaded,
        alertState,
        setAlertState,
        exportAlertState,
        setExportAlertState,
        orderDetailsRequestParams,
        orderIndex,
        nickName,
        localDelivery,
        orderBarcodeApiState,
        deliveryTrackerLoaded,
        orderNumber,
      }}
    >
      {children}
    </OrderDetailsContext.Provider>
  );
};
